import { useEffect, useMemo, useState } from 'react';
import {
  fetchDesktopReleases,
  type DesktopPlatform,
  type DesktopRelease,
  type NixDesktopSource,
} from './api';
import { detectDesktopPlatform } from './platform';
import { PlatformIcon, PlatformPicker, platformLabels } from './platform-picker';
import { WorkspaceIcon } from '@/workspace/icons';
import { DownloadVerification } from './download-verification';
import { useNamespaceCopy } from '@/i18n/ns';
import { entrySurfaceCopy } from '@/i18n/ns/entry-surfaces';

const formats: Record<DesktopPlatform, string[]> = {
  linux: ['AppImage', 'deb', 'rpm', 'tar.gz'],
  macos: ['dmg'],
  windows: ['exe'],
};

function ReleaseLink({ release }: { release: DesktopRelease }) {
  const copy = useNamespaceCopy(entrySurfaceCopy);
  const [copied, setCopied] = useState(false);
  const arch =
    release.platform === 'macos'
      ? release.arch === 'arm64'
        ? 'Apple silicon'
        : 'Intel'
      : release.arch;
  const copyChecksum = async () => {
    if (!release.sha256 || !navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(release.sha256);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div className="desktop-download-release">
      <a className="desktop-download-link" href={release.url} target="_blank" rel="noreferrer">
        <span>
          <strong>
            {release.platform === 'macos'
              ? copy.downloadFor.replace('{arch}', arch)
              : copy.downloadFormat.replace('{format}', release.format)}
          </strong>
          <small>
            {release.platform === 'macos' ? copy.dmgInstaller : arch} · v{release.version}
          </small>
        </span>
        <WorkspaceIcon name="download" />
      </a>
      {release.sha256 ? (
        <details>
          <summary>{copy.checksum}</summary>
          <code>{release.sha256}</code>
          <button type="button" className="company-link" onClick={() => void copyChecksum()}>
            {copied ? copy.copied : copy.copyChecksum}
          </button>
        </details>
      ) : null}
      <DownloadVerification verification={release.verification} />
    </div>
  );
}

function NixInstructions({ nix }: { nix: NixDesktopSource }) {
  const copy = useNamespaceCopy(entrySurfaceCopy);
  return (
    <section className="desktop-nix" data-testid="desktop-nix">
      <h2>NixOS</h2>
      <p>{copy.nixNote}</p>
      <pre>
        <code>{`nix run '${nix.sourceUrl}'`}</code>
      </pre>
      <pre>
        <code>{`inputs.poneva-desktop.url = "${nix.sourceUrl}";`}</code>
      </pre>
      <p>{copy.nixUpdateInstruction}</p>
      <pre>
        <code>nix flake update poneva-desktop</code>
      </pre>
      <p className="desktop-downloads-note">
        {copy.sourceVersion.replace('{version}', nix.version)} <code>{nix.sha256}</code>
      </p>
      <DownloadVerification verification={nix.verification} />
    </section>
  );
}

export function DesktopDownloads() {
  const copy = useNamespaceCopy(entrySurfaceCopy);
  const [detected] = useState(() => detectDesktopPlatform(navigator));
  const [selected, setSelected] = useState<DesktopPlatform | null>(detected);
  const [releases, setReleases] = useState<DesktopRelease[] | null>(null);
  const [nix, setNix] = useState<NixDesktopSource | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetchDesktopReleases(controller.signal)
      .then((result) => {
        setReleases(result.releases);
        setNix(result.nix ?? null);
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof DOMException && cause.name === 'AbortError')) setError(true);
      });
    return () => controller.abort();
  }, []);

  const grouped = useMemo(() => {
    const result = new Map<DesktopPlatform, DesktopRelease[]>();
    for (const release of releases ?? []) {
      if (!formats[release.platform].includes(release.format)) continue;
      result.set(release.platform, [...(result.get(release.platform) ?? []), release]);
    }
    return result;
  }, [releases]);

  return (
    <div className="desktop-downloads" data-testid="desktop-downloads">
      <h1>{copy.downloads}</h1>
      <p className="company-description">{copy.desktopDescription}</p>
      <p className="desktop-platform-hint">
        {detected
          ? copy.detectedPlatformHint.replace('{platform}', platformLabels[detected])
          : copy.choosePlatformHint}
      </p>
      <PlatformPicker selected={selected} onSelect={setSelected} />
      {releases === null && !error ? <p role="status">{copy.checkingDownloads}</p> : null}
      {error ? (
        <p role="alert" className="company-error">
          {copy.unavailable}
        </p>
      ) : null}
      {releases !== null && !error && !selected && grouped.size === 0 && !nix ? (
        <div className="desktop-downloads-empty" role="status">
          <strong>{copy.notPublished}</strong>
          <p>{copy.webReady}</p>
        </div>
      ) : null}
      {releases !== null && !error && selected ? (
        <section
          className="desktop-download-card"
          aria-label={copy.platformDownloads.replace('{platform}', platformLabels[selected])}
        >
          <div className="desktop-download-heading">
            <span className="desktop-platform-emblem">
              <PlatformIcon platform={selected} />
            </span>
            <div>
              <h2>{copy.ponevaFor.replace('{platform}', platformLabels[selected])}</h2>
              {selected === detected ? (
                <span className="desktop-detected-badge">{copy.forDevice}</span>
              ) : null}
            </div>
          </div>
          {selected === 'macos' ? <p>{copy.chooseMac}</p> : null}
          <div className="desktop-download-list">
            {(grouped.get(selected) ?? []).map((release) => (
              <ReleaseLink
                key={`${release.platform}-${release.version}-${release.arch}-${release.format}`}
                release={release}
              />
            ))}
          </div>
          {!grouped.get(selected)?.length ? (
            <p role="status">{copy.installersUnavailable}</p>
          ) : null}
          {selected === 'linux' && nix ? <NixInstructions nix={nix} /> : null}
          {selected === 'linux' && !nix ? (
            <p className="desktop-downloads-note" data-testid="desktop-nix-unpublished">
              {copy.nixUnpublished}
            </p>
          ) : null}
        </section>
      ) : null}
      <p className="desktop-downloads-note">{copy.workspaceNote}</p>
    </div>
  );
}
